import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

const SelectField = ({
  label,
  value,
  onChange,
  defaultOption,
  options,
  error,
  name,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  const optionsArray =
    !Array.isArray(options) && typeof options === "object"
      ? Object.keys(options).map((optionName) => ({
          label: options[optionName].name,
          value: options[optionName]._id,
        }))
      : options;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSelect = (optionValue) => {
    onChange({ name, value: optionValue });
    setIsOpen(false);
  };

  const selectedLabel =
    optionsArray?.find((opt) => opt.value === value)?.label || defaultOption;

  return (
    <div className="mt-4 w-full relative" ref={selectRef}>
      <label className="form-label text-xl sm:text-lg block mb-2 font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>

      {/* Мобильно-оптимизированный триггер */}
      <div
        className="bg-gray-50 border border-gray-300 text-gray-900 
                   text-lg rounded-lg 
                    w-full p-4
                   dark:bg-gray-700 dark:border-gray-600 
                   dark:text-white 
                   cursor-pointer
                   flex justify-between items-center
                   touch-manipulation" // Улучшает поведение касания
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === "Enter" && setIsOpen(!isOpen)}
      >
        <span className="truncate">{selectedLabel}</span>
        <svg
          className={`w-6 h-6 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Адаптивный выпадающий список */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {optionsArray &&
            optionsArray.map((option) => (
              <div
                key={option.value}
                className={`p-4 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-600 text-lg
                         active:bg-blue-100 active:scale-95 transition-transform
                         min-h-[44px] flex items-center`} // Минимальная высота для удобного касания
                onClick={() => handleSelect(option.value)}
                role="option"
                aria-selected={value === option.value}
              >
                {option.label}
              </div>
            ))}
        </div>
      )}

      {/* Скрытый нативный селект для форм */}
      <select
        className="hidden"
        name={name}
        value={value}
        onChange={() => {}}
        required
      >
        <option value="">{defaultOption}</option>
        {optionsArray &&
          optionsArray.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
      </select>

      {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
    </div>
  );
};

SelectField.propTypes = {
  defaultOption: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.string,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  name: PropTypes.string,
};

export default SelectField;
